import React, { useMemo, useState } from 'react';
import { XMarkIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';

// Modale per spostare un nodo sotto un nuovo responsabile.
// Props: nodo, nodi (array completo della rete), onClose, onConferma(nuovoParentId, gestioneFigli), loading
export default function SpostaNodoModal({ nodo, nodi = [], onClose, onConferma, loading = false }) {
  const [nuovoParentId, setNuovoParentId] = useState('');
  const [gestioneFigli, setGestioneFigli] = useState('porta_ramo');

  // ID del nodo + tutti i suoi discendenti (da escludere: non può finire sotto se stesso)
  const idEsclusi = useMemo(() => {
    const esclusi = new Set([nodo.id]);
    let cambiato = true;
    while (cambiato) {
      cambiato = false;
      for (const n of nodi) {
        if (n.parentId != null && esclusi.has(n.parentId) && !esclusi.has(n.id)) {
          esclusi.add(n.id);
          cambiato = true;
        }
      }
    }
    return esclusi;
  }, [nodo, nodi]);

  const haFigli = useMemo(() => nodi.some(n => n.parentId === nodo.id), [nodi, nodo]);

  const nomeNodo = (n) => {
    if (n.nome) return n.nome;
    if (n.utente) return `${n.utente.nome || ''} ${n.utente.cognome || ''}`.trim() || n.utente.username || `Nodo #${n.id}`;
    return `Nodo #${n.id}`;
  };

  // Candidati = tutti i nodi attivi tranne il nodo stesso e i suoi discendenti (anti-ciclo)
  const candidati = useMemo(
    () => nodi
      .filter(n => n.attivo !== false && !idEsclusi.has(n.id))
      .sort((a, b) => (a.livello - b.livello) || nomeNodo(a).localeCompare(nomeNodo(b))),
    [nodi, idEsclusi]
  );

  const parentAttuale = nodi.find(n => n.id === nodo.parentId);

  const conferma = () => {
    if (!nuovoParentId) return;
    onConferma(parseInt(nuovoParentId, 10), haFigli ? gestioneFigli : 'porta_ramo');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <ArrowsRightLeftIcon className="h-5 w-5 text-blue-600" /> Sposta nella rete
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Corpo */}
        <div className="px-6 py-5 space-y-5">
          <div className="text-sm text-gray-600">
            Stai spostando <span className="font-semibold text-gray-900">{nomeNodo(nodo)}</span>
            {parentAttuale
              ? <> — attualmente sotto <span className="font-medium text-gray-800">{nomeNodo(parentAttuale)}</span>.</>
              : <> (nodo radice).</>}
          </div>

          {/* Nuovo responsabile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nuovo responsabile</label>
            <select
              value={nuovoParentId}
              onChange={e => setNuovoParentId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            >
              <option value="">— Seleziona il nuovo responsabile —</option>
              {candidati.map(n => (
                <option key={n.id} value={n.id}>
                  {' '.repeat((n.livello || 0) * 2)}{nomeNodo(n)}
                </option>
              ))}
            </select>
            {candidati.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">Nessun responsabile disponibile per questo nodo.</p>
            )}
          </div>

          {/* Gestione sottoposti (solo se il nodo ne ha) */}
          {haFigli && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">I suoi sottoposti…</label>
              <div className="space-y-2">
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  style={{ borderColor: gestioneFigli === 'porta_ramo' ? '#2563eb' : '#e5e7eb' }}>
                  <input type="radio" name="gestioneFigli" value="porta_ramo" checked={gestioneFigli === 'porta_ramo'}
                    onChange={() => setGestioneFigli('porta_ramo')} className="mt-1" disabled={loading} />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">Si spostano con lui</span>
                    <span className="block text-xs text-gray-500">L'intero ramo (lui + tutti i sottoposti) passa sotto il nuovo responsabile.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  style={{ borderColor: gestioneFigli === 'riassegna' ? '#2563eb' : '#e5e7eb' }}>
                  <input type="radio" name="gestioneFigli" value="riassegna" checked={gestioneFigli === 'riassegna'}
                    onChange={() => setGestioneFigli('riassegna')} className="mt-1" disabled={loading} />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">Restano al responsabile attuale</span>
                    <span className="block text-xs text-gray-500">Solo questa persona si sposta; i suoi sottoposti vengono ri-agganciati a {parentAttuale ? nomeNodo(parentAttuale) : 'il responsabile attuale'}.</span>
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50/50">
          <button onClick={onClose} disabled={loading}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors disabled:opacity-50">
            Annulla
          </button>
          <button onClick={conferma} disabled={loading || !nuovoParentId}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
            {loading ? 'Spostamento…' : 'Sposta'}
          </button>
        </div>
      </div>
    </div>
  );
}
